import version from "version";
import Variables from "./variables";
import Request from "./request";
import API from "./api";
import Interface from "./interface";
import Common from "./common";
import LiveEvent from "./liveevent";
import Log from "./log";

const FCA_EVENT = /^fca\./;
const pendingPromises = {};
const ids = {};

export default class FacilioApp {
  constructor(options) {
    this._origin = options.origin;
    this._source = window.ReactNativeWebView ? window.ReactNativeWebView : window.parent;
    this._cappId = options.cappId;
    this._context = null;
    this._widget = null;
    this._isExportMode = false;
    this._isMobile = window.ReactNativeWebView ? true : false;
    this._currentUser = null;
    this._currentOrg = null;
    this._currentApp = null;
    this._currentTheme = 'light';
    this._messageHandlers = {};

    this.common = new Common(this);
    this.variables = new Variables(this);
    this.request = new Request(this);
    this.api = new API(this);
    this.interface = new Interface(this);
    this.liveevent = new LiveEvent(this);
    this.log = new Log(this);

    this.ready = false;

    this.on(
      "app.loaded",
      (data) => {
        this.ready = true;
        this._context = data.context;
        this._widget = data.widget;
        this._isExportMode = data.exportMode;
        this._currentUser = data.currentUser;
        this._currentOrg = data.currentOrg;
        this._currentApp = data.currentApp
        this._currentTheme = data.currentTheme || 'light';
      },
      this
    );

    window.addEventListener("message", messageHandler.bind(null, this));
    this._postMessage("app.handshake", { version: version });
  }

  on(name, handler, context) {
    if (typeof handler === "function") {
      handler = context ? handler.bind(context) : handler;

      this._messageHandlers[name] = this._messageHandlers[name] || [];
      this._messageHandlers[name].push(handler);
    }
  }

  off(name, handler) {
    if (!this._messageHandlers[name]) {
      return false;
    }
    const index = this._messageHandlers[name].indexOf(handler);
    if (this.has(name, handler)) {
      this._messageHandlers[name].splice(index, 1);
    }
    return handler;
  }

  has(name, handler) {
    if (!this._messageHandlers[name]) {
      return false;
    }
    return this._messageHandlers[name].indexOf(handler) !== -1;
  }

  getContext() {
    return this._context;
  }

  getWidget() {
    return this._widget;
  }

  isExportMode() {
    return this._isExportMode;
  }

  isMobile() {
    return this._isMobile;
  }

  getCurrentUser() {
    return this._currentUser;
  }

  getCurrentTheme() {
    return this._currentTheme;
  }

  getCurrentOrg() {
    return this._currentOrg;
  }

   getCurrentApp() {
    return this._currentApp;
  }

  _nextIdFor(name) {
    if (isNaN(ids[name])) {
      ids[name] = 0;
    }
    return ++ids[name];
  }

  _postMessageWithPromise(key, params) {
    const id = this._nextIdFor("promise");
    let timeoutId, promiseStartTime;
    const client = this;

    const promise = new Promise((resolve, reject) => {
      promiseStartTime = window.performance.now();

      pendingPromises[id] = { resolve, reject };

      const msg = JSON.stringify({
        id: id,
        key: key,
        params: params,
        instanceUid: this._cappId,
      });
      if (this._isMobile) {
        this._source.postMessage(msg);
      }
      else {
        this._source.postMessage(msg, this._origin);
      }
    });
    return promise;
  }

  _postMessage(name, data) {
    const msg = JSON.stringify({
      key: name,
      params: data,
      instanceUid: this._cappId,
    });
    if (this._isMobile) {
      this._source.postMessage(msg);
    }
    else {
      this._source.postMessage(msg, this._origin);
    }
  }
}

function isValidEvent(client, event) {
  if (client._isMobile && window.ReactNativeWebView) {
    // For mobile, we assume the event is valid if it comes from the ReactNativeWebView
    return true;
  }
  return (
    client && client._origin === event.origin && client._source === event.source
  );
}

function triggerEvent(client, eventName, data) {
  if (!client._messageHandlers[eventName]) {
    return false;
  }
  client._messageHandlers[eventName].forEach((handler) => {
    handler(data);
  });
}

function finalizePendingPromise(pendingPromise, data) {
  if (data.error) {
    const error =
      typeof data.error === "object"
        ? new Error(data.error.message)
        : new Error(data.error);
    pendingPromise.reject(error);
  } else {
    pendingPromise.resolve(data.result);
  }

  delete pendingPromises[data.id];
}

function messageHandler(client, event) {
  if (!isValidEvent(client, event)) {
    return;
  }

  let data = event.data;

  if (!data) {
    return;
  }

  if (typeof data === "string") {
    try {
      data = JSON.parse(event.data);
    } catch (e) {
      return e;
    }
  }

  let pendingPromise;
  if (client.isExportMode()) {
    if (FCA_EVENT.test(data.key)) {
      const key = data.key.replace(FCA_EVENT, "");
      if (data.id && (pendingPromise = pendingPromises[data.id])) {
        finalizePendingPromise(pendingPromise, data);
      } else {
        triggerEvent(client, key, data.message);
      }
    }
  } else {
    if (data.id && (pendingPromise = pendingPromises[data.id])) {
      finalizePendingPromise(pendingPromise, data);
    } else if (FCA_EVENT.test(data.key)) {
      const key = data.key.replace(FCA_EVENT, "");

      triggerEvent(client, key, data.message);
    }
  }
}
